import React from 'react';
import { Box, Typography, TextField, Paper, Tooltip } from '@mui/material';
import { TouchApp, Notes } from '@mui/icons-material';
import InfoIcon from '@mui/icons-material/Info';
import { PROMPTING_TOOLTIPS } from '../../../../constants/GoalsConstants';

import ValueInput from '../ValueInput';
import TrialsInput from '../TrialsInput';
import CriteriaFields from '../CriteriaFields';
import PromptingFields from '../PromptingFields';
import PhysicalAssistanceFields from '../PhysicalAssistanceFields';
import DataPointOptions from '../DataPointOptions';
import TaskAnalysisFields from '../TaskAnalysisFields';
import Interventions from '../Interventions';

const DataPointItem = ({
  dataPoint,
  index,
  goalType,
  measurementUnit,
  trialsMax,
  promptingType,
  existingPromptingType,
  handleFieldChange,
  handleTrialChange,
  handleAddTrial,
  handleCriteriaChange,
  handlePromptingChange,
  handleCheckboxChange,
  handleAbcChange,
  handleTimeChange,
  goalPromptingLevel,
  prompting,
  allGoalData,
  user,
  customSections = [],
  isLoadingCustomSections = false,
}) => {
  const preventNumberScroll = (event) => {
    event.preventDefault();
    event.target.blur();
  };

  // Filter custom sections based on the IDs stored in the goal
  const customDataEntrySections =
    allGoalData?.DataEntrySelection?.CustomDataEntrySections ||
    allGoalData?.dataCollection?.DataEntrySelection?.CustomDataEntrySections ||
    [];

  // eslint-disable-next-line no-unused-vars
  const selectedCustomSections = customSections.filter((section) =>
    customDataEntrySections.includes(section.docId)
  );

  // Debug logs removed after verification
  // console.log(dataPoint.notes,'datapoint');
  return (
    <>
      <ValueInput
        dataPoint={dataPoint}
        index={index}
        goalType={goalType}
        measurementUnit={measurementUnit}
        handleFieldChange={handleFieldChange}
      />

      <TrialsInput
        dataPoint={dataPoint}
        index={index}
        goalType={goalType}
        trialsMax={trialsMax}
        handleTrialChange={handleTrialChange}
        handleAddTrial={handleAddTrial}
        allGoalData={allGoalData}
      />

      <PromptingFields
        dataPoint={dataPoint}
        index={index}
        promptingType={promptingType}
        existingPromptingType={existingPromptingType}
        handlePromptingChange={handlePromptingChange}
        handleFieldChange={handleFieldChange}
        goalType={goalType}
        goalPromptingLevel={goalPromptingLevel}
        prompting={prompting}
        allGoalData={allGoalData}
      />

      <PhysicalAssistanceFields
        dataPoint={dataPoint}
        index={index}
        handleFieldChange={handleFieldChange}
        goalType={goalType}
        allGoalData={allGoalData}
      />

      <CriteriaFields
        dataPoint={dataPoint}
        index={index}
        handleCriteriaChange={handleCriteriaChange}
      />

      {(() => {
        let promptingTypeValue = promptingType;
        if (dataPoint.currentGoal && dataPoint.currentGoal !== 'main') {
          const subgoals =
            allGoalData?.HasSubgoal?.Subgoals ||
            allGoalData?.dataCollection?.HasSubgoal?.Subgoals ||
            [];
          const subgoal = subgoals.find(
            (sg) => String(sg.SubgoalNumber) === String(dataPoint.currentGoal)
          );
          if (subgoal?.PromptingApplicable?.isPromptingApplicable === 'Yes') {
            promptingTypeValue = subgoal.PromptingApplicable.PromptingType;
          }
        }

        if (promptingTypeValue === 'P+ & P-') {
          return (
            <Paper
              elevation={1}
              sx={{
                p: 2,
                mb: 2,
                backgroundColor: 'transparent',
                border: '1px solid #e5e7eb',
                borderRadius: '8px',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <TouchApp sx={{ color: '#6b7280', mr: 1, fontSize: 18 }} />
                <Typography
                  variant='body1'
                  sx={{
                    color: '#1f2937',
                    fontWeight: 400,
                    fontSize: '1rem',
                  }}
                >
                  P+ & P- Values
                </Typography>
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography
                    variant='body1'
                    sx={{
                      color: '#374151',
                      fontWeight: 500,
                      minWidth: '30px',
                    }}
                  >
                    P-
                  </Typography>
                  <TextField
                    type='number'
                    value={dataPoint.prompting?.pMinus || ''}
                    onChange={(e) =>
                      handlePromptingChange(index, 'pMinus', e.target.value)
                    }
                    inputProps={{ onWheel: preventNumberScroll }}
                    sx={{
                      width: 100,
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: 'white',
                        '&:hover': {
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#6b7280',
                          },
                        },
                        '&.Mui-focused': {
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#6b7280',
                          },
                        },
                      },
                    }}
                  />
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography
                    variant='body1'
                    sx={{
                      color: '#374151',
                      fontWeight: 500,
                      minWidth: '30px',
                    }}
                  >
                    P+
                  </Typography>
                  <TextField
                    type='number'
                    value={dataPoint.prompting?.pPlus || ''}
                    onChange={(e) =>
                      handlePromptingChange(index, 'pPlus', e.target.value)
                    }
                    inputProps={{ onWheel: preventNumberScroll }}
                    sx={{
                      width: 100,
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: 'white',
                        '&:hover': {
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#6b7280',
                          },
                        },
                        '&.Mui-focused': {
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#6b7280',
                          },
                        },
                      },
                    }}
                  />
                </Box>
              </Box>
            </Paper>
          );
        }

        if (promptingTypeValue === 'I, VP (IVP, DVP, PVP), GP, PP, ViP, M') {
          return (
            <Paper
              elevation={1}
              sx={{
                p: 2,
                mb: 2,
                backgroundColor: 'transparent',
                border: '1px solid #e5e7eb',
                borderRadius: '8px',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <TouchApp sx={{ color: '#6b7280', mr: 1, fontSize: 18 }} />
                <Typography
                  variant='body1'
                  sx={{
                    color: '#1f2937',
                    fontWeight: 400,
                    fontSize: '1rem',
                  }}
                >
                  I, VP (IVP, DVP, PVP), GP, PP, ViP, M Values
                </Typography>
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 2,
                }}
              >
                {['I', 'VP', 'IVP', 'DVP', 'PVP', 'GP', 'PP', 'ViP', 'M'].map(
                  (label) => (
                    <Box
                      key={label}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        minWidth: '120px',
                      }}
                    >
                      <Typography
                        variant='body2'
                        sx={{
                          color: '#374151',
                          fontWeight: 500,
                          minWidth: '30px',
                          display: 'inline-flex',
                          alignItems: 'center',
                        }}
                      >
                        {label}
                        <Tooltip title={PROMPTING_TOOLTIPS[label]}>
                          <InfoIcon
                            sx={{
                              fontSize: 'small',
                              ml: 0.5,
                              verticalAlign: 'middle',
                              cursor: 'help',
                            }}
                          />
                        </Tooltip>
                      </Typography>
                      <TextField
                        type='number'
                        value={dataPoint.prompting?.[label] || ''}
                        onChange={(e) =>
                          handlePromptingChange(index, label, e.target.value)
                        }
                        inputProps={{ onWheel: preventNumberScroll }}
                        sx={{
                          width: 80,
                          '& .MuiOutlinedInput-root': {
                            backgroundColor: 'white',
                            '&:hover': {
                              '& .MuiOutlinedInput-notchedOutline': {
                                borderColor: '#6b7280',
                              },
                            },
                            '&.Mui-focused': {
                              '& .MuiOutlinedInput-notchedOutline': {
                                borderColor: '#6b7280',
                              },
                            },
                          },
                        }}
                      />
                    </Box>
                  )
                )}
              </Box>
            </Paper>
          );
        }
        return null;
      })()}

      <Interventions
        dataPoint={dataPoint}
        index={index}
        handleFieldChange={handleFieldChange}
        allGoalData={allGoalData}
      />

      <DataPointOptions
        dataPoint={dataPoint}
        index={index}
        handleCheckboxChange={handleCheckboxChange}
        handleAbcChange={handleAbcChange}
        handleFieldChange={handleFieldChange}
        handleTimeChange={handleTimeChange}
        allGoalData={allGoalData}
        user={user}
        customSections={customSections}
        isLoadingCustomSections={isLoadingCustomSections}
      />

      <TaskAnalysisFields
        allGoalData={allGoalData}
        dataPoint={dataPoint}
        index={index}
        handleFieldChange={handleFieldChange}
      />

      <Box
        sx={{
          gap: 2,
          marginTop: '16px',
          marginBottom: '16px',
          padding: '16px',
          backgroundColor: '#f8f9ff',
          borderRadius: '8px',
          border: '1px solid #e0e0e0',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            marginBottom: '12px',
          }}
        >
          <Notes sx={{ color: '#6b7280', fontSize: 18 }} />
          <Typography
            variant='body1'
            sx={{
              color: '#1f2937',
              fontWeight: 500,
              fontSize: '1rem',
            }}
          >
            Notes:
          </Typography>
        </Box>
        <TextField
          fullWidth
          label='Add your notes here...'
          margin='dense'
          value={dataPoint.notes}
          multiline
          rows={3}
          sx={{
            '& .MuiOutlinedInput-root': {
              backgroundColor: 'white',
              borderRadius: '6px',
              '&:hover': {
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#6b7280',
                },
              },
              '&.Mui-focused': {
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#9291ef',
                },
              },
            },
          }}
          onChange={(e) => handleFieldChange(index, 'notes', e.target.value)}
        />
      </Box>
    </>
  );
};

export default DataPointItem;
